import { Menu, MenuItem } from '@mui/material';
import {
  RECIPE_GROUP_DESCRIPTIONS,
  RECIPE_GROUP_LABELS,
  RECIPE_GROUP_ORDER,
  RECIPE_GROUP_TRANSFORMS_AGGREGATE,
} from 'js/constants/schemaEditor';
import { useIsOrgFeatureEnabled } from 'new/hooks/adapters/useIsOrgFeatureEnabled';
import React from 'react';
import Tooltip from '../elements/mui/Tooltip';

interface RecipeTypeMenuProps {
  open: boolean;
  anchorEl: HTMLElement | null;
  onClose: () => void;
  onRecipeTypeClicked: (type: string) => void;
  isRecipeGroupOptionDisabled: (type: string) => {
    disabled: boolean;
    message?: string;
  };
  getRecipeTypeIcon: (type: string) => React.ReactNode;
}

const ConfigureRecipeTypeMenu: React.FC<RecipeTypeMenuProps> = ({
  open,
  anchorEl,
  onClose,
  onRecipeTypeClicked,
  isRecipeGroupOptionDisabled,
  getRecipeTypeIcon,
}) => {
  const isSqlTransformEnabled = useIsOrgFeatureEnabled('sql_transform');

  /**
   * @Guard: Filter RECIPE_GROUP_ORDER based on SQL Transform feature flag
   * Only show aggregate when feature is explicitly enabled (true)
   * Hide for: false, null, undefined
   */
  const availableRecipeTypes = React.useMemo(() => {
    if (isSqlTransformEnabled !== true) {
      return RECIPE_GROUP_ORDER.filter((type) => type !== RECIPE_GROUP_TRANSFORMS_AGGREGATE);
    }

    return RECIPE_GROUP_ORDER;
  }, [isSqlTransformEnabled]);

  return (
    <Menu
      className="recipe-type-menu"
      open={open}
      anchorEl={anchorEl}
      keepMounted
      anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'left',
      }}
      transformOrigin={{
        vertical: 'top',
        horizontal: 'left',
      }}
      onClose={onClose}
    >
      {availableRecipeTypes.map((type) => {
        const { disabled, message } = isRecipeGroupOptionDisabled(type);

        const menuItem = (
          <MenuItem
            key={type}
            data-testid={`recipe-type-menu-item-${type}`}
            className="recipe-builder-menu-item recipe-type-menu-item"
            disabled={disabled}
            onClick={() => onRecipeTypeClicked(type)}
          >
            <div className="icon">{getRecipeTypeIcon(type)}</div>
            <div>
              <div className="recipe-type-name">{RECIPE_GROUP_LABELS[type]}</div>
              <div className="recipe-type-description">{RECIPE_GROUP_DESCRIPTIONS[type]}</div>
            </div>
          </MenuItem>
        );

        if (disabled && message) {
          return (
            // @ts-ignore
            <Tooltip key={type} title={message} arrow={true} placement="right">
              <span>{menuItem}</span>
            </Tooltip>
          );
        }

        return menuItem;
      })}
    </Menu>
  );
};

export default ConfigureRecipeTypeMenu;
